export type Locale = "es" | "ca";

export interface PublicCopy {
  htmlLang: string;
  intlLocale: string;
  staticText: Record<string, string>;
  loadingToday: string;
  articlesFailed: (status: number) => string;
  noStories: string;
  todayEdition: string;
  editionDescription: string;
  by: string;
  readMore: string;
  moreReporting: string;
  forAgents: string;
  agentTitle: string;
  agentDescription: string;
  discoverMcp: string;
  agentGuide: string;
  openingStory: string;
  storyNotFound: string;
  storyFailed: (status: number) => string;
  usePolicy: string;
  searchAllowed: string;
  aiInputAllowed: string;
  aiTrainingDenied: string;
  liveEnabled: string;
  showLivePlayer: string;
  hideLivePlayer: string;
  liveDescription: string;
  liveFrameTitle: string;
  noLive: string;
  watch: string;
  latestVideos: string;
  videoDescription: string;
  illustrationFor: string;
  aiGeneratedImage: string;
  openArticle: string;
  premiumArticle: string;
  adSupported: string;
  subscriptionIncluded: string;
  subscriptionTitle: string;
  subscriptionDescription: string;
  subscriptionButton: string;
  advertisement: string;
  advertisementDescription: string;
  agentLicenses: string;
  agentLicenseDescription: string;
  agentSearch: string;
  aiTraining: string;
  licenseUse: string;
  intentHelpLabel: string;
  intentTitle: string;
  intentDescription: string;
  searchIntentExplanation: string;
  trainingIntentExplanation: string;
  intentLimit: string;
  close: string;
  searching: string;
  searchFailed: (status: number) => string;
  noResults: string;
  returnHome: string;
  unableToLoad: string;
  retry: string;
  unexpectedError: string;
  missingElement: (selector: string) => string;
}

const sharedSpanishStatic = {
  skip: "Ir a las noticias",
  noLive: "Sin transmisión en vivo",
  openSections: "Abrir secciones",
  home: "Inicio de Rambla Oberta",
  searchToggle: "Buscar en el archivo",
  world: "Mundo",
  politics: "Política",
  climate: "Clima",
  culture: "Cultura",
  technology: "Tecnología",
  about: "Acerca de",
  searchLabel: "Buscar en nuestras noticias",
  searchPlaceholder: "Prueba con ‘acceso al agua’ o ‘licencias de IA’",
  searchButton: "Buscar",
  independent: "Periodismo independiente",
  initialLoading: "Cargando las noticias de hoy…",
  footerTagline: "Periodismo independiente para las personas y sus agentes.",
  forAgents: "Para agentes",
  mcpServer: "Servidor MCP",
  contentPolicy: "Política de contenido",
};

export const COPY: Record<Locale, PublicCopy> = {
  es: {
    htmlLang: "es-ES",
    intlLocale: "es-ES",
    staticText: sharedSpanishStatic,
    loadingToday: "Cargando las noticias de hoy…",
    articlesFailed: (status) => `No se pudieron cargar los artículos: ${status}`,
    noStories: "Todavía no hay noticias publicadas.",
    todayEdition: "La edición de hoy",
    editionDescription:
      "Periodismo con procedencia visible, permisos explícitos para la IA y licencias directas.",
    by: "Por",
    readMore: "Leer más",
    moreReporting: "Más noticias",
    forAgents: "Para agentes de IA",
    agentTitle: "Busca sin coste. Licencia el periodismo que necesitas.",
    agentDescription:
      "Conéctate a nuestro servidor MCP para consultar titulares, resúmenes, precios y políticas de contenido. Las noticias de pago se entregan mediante x402 con un comprobante.",
    discoverMcp: "Descubrir MCP",
    agentGuide: "Leer la guía para agentes",
    openingStory: "Abriendo la noticia…",
    storyNotFound: "No encontramos esa noticia.",
    storyFailed: (status) => `No se pudo cargar la noticia: ${status}`,
    usePolicy: "Política de uso",
    searchAllowed: "Búsqueda: permitida",
    aiInputAllowed: "Uso por IA: permitido",
    aiTrainingDenied: "Entrenamiento de IA: no permitido",
    liveEnabled: "En directo ahora",
    showLivePlayer: "Mostrar reproductor en directo",
    hideLivePlayer: "Ocultar reproductor en directo",
    liveDescription:
      "La redacción ha habilitado esta señal de Cloudflare Stream. La reproducción comienza cuando se conecta el codificador.",
    liveFrameTitle: "En directo",
    noLive: "Sin transmisión en directo",
    watch: "Ver",
    latestVideos: "Últimos vídeos",
    videoDescription:
      "Vídeos y subtítulos generados con IA, entregados mediante Cloudflare Stream.",
    illustrationFor: "Ilustración para",
    aiGeneratedImage: "Imagen generada con Workers AI",
    openArticle: "Abierto",
    premiumArticle: "Premium",
    adSupported: "Con publicidad",
    subscriptionIncluded: "Con suscripción",
    subscriptionTitle: "Contenido para suscriptores",
    subscriptionDescription:
      "Esta noticia está incluida en la suscripción. No tiene un precio humano por lectura.",
    subscriptionButton: "Acceder con suscripción",
    advertisement: "Publicidad",
    advertisementDescription: "Este espacio ayuda a financiar el acceso abierto para lectores humanos.",
    agentLicenses: "Licencias para agentes",
    agentLicenseDescription:
      "Los agentes utilizan la misma URL que los lectores. Un bot se interpreta como búsqueda; el entrenamiento se declara mediante una cabecera.",
    agentSearch: "Búsqueda agéntica",
    aiTraining: "Entrenamiento de IA",
    licenseUse: "Licenciar este uso",
    intentHelpLabel: "Cómo se captura la intención de uso",
    intentTitle: "¿Cómo se captura la intención?",
    intentDescription:
      "Humanos y agentes comparten la URL canónica. Cloudflare Bot Management, los agentes conocidos o Accept: text/markdown activan búsqueda; X-License-Intent: ai_training declara entrenamiento.",
    searchIntentExplanation:
      "Búsqueda agéntica entrega un resumen del autor o generado con Workers AI y, como máximo, una URL de imagen. No entrega el cuerpo ni concede derechos de entrenamiento.",
    trainingIntentExplanation:
      "Entrenamiento de IA entrega el artículo completo como Markdown estructurado y concede su uso para entrenar o ajustar modelos.",
    intentLimit:
      "La intención es una declaración de licencia auditable, no una prueba técnica de lo que el comprador hace después de descargar el contenido.",
    close: "Cerrar",
    searching: "Buscando en el archivo…",
    searchFailed: (status) => `La búsqueda ha fallado: ${status}`,
    noResults: "No encontramos noticias que coincidan.",
    returnHome: "Volver al inicio",
    unableToLoad: "No se pudo cargar",
    retry: "Intentar de nuevo",
    unexpectedError: "Se ha producido un error inesperado.",
    missingElement: (selector) => `Falta un elemento requerido: ${selector}`,
  },
  ca: {
    htmlLang: "ca-ES",
    intlLocale: "ca-ES",
    staticText: {
      skip: "Anar a les notícies",
      noLive: "Sense emissió en directe",
      openSections: "Obrir seccions",
      home: "Inici de Rambla Oberta",
      searchToggle: "Cercar a l’arxiu",
      world: "Món",
      politics: "Política",
      climate: "Clima",
      culture: "Cultura",
      technology: "Tecnologia",
      about: "Sobre nosaltres",
      searchLabel: "Cercar a les nostres notícies",
      searchPlaceholder: "Prova ‘accés a l’aigua’ o ‘llicències d’IA’",
      searchButton: "Cercar",
      independent: "Periodisme independent",
      initialLoading: "Carregant les notícies d’avui…",
      footerTagline: "Periodisme independent per a les persones i els seus agents.",
      forAgents: "Per a agents",
      mcpServer: "Servidor MCP",
      contentPolicy: "Política de continguts",
    },
    loadingToday: "Carregant les notícies d’avui…",
    articlesFailed: (status) => `No s’han pogut carregar els articles: ${status}`,
    noStories: "Encara no hi ha notícies publicades.",
    todayEdition: "L’edició d’avui",
    editionDescription:
      "Periodisme amb procedència visible, permisos explícits per a la IA i llicències directes.",
    by: "Per",
    readMore: "Llegir més",
    moreReporting: "Més notícies",
    forAgents: "Per a agents d’IA",
    agentTitle: "Cerca sense cost. Llicencia el periodisme que necessites.",
    agentDescription:
      "Connecta’t al nostre servidor MCP per consultar titulars, resums, preus i polítiques de contingut. Les notícies de pagament es lliuren mitjançant x402 amb un comprovant.",
    discoverMcp: "Descobrir MCP",
    agentGuide: "Llegir la guia per a agents",
    openingStory: "Obrint la notícia…",
    storyNotFound: "No hem trobat aquesta notícia.",
    storyFailed: (status) => `No s’ha pogut carregar la notícia: ${status}`,
    usePolicy: "Política d’ús",
    searchAllowed: "Cerca: permesa",
    aiInputAllowed: "Ús per IA: permès",
    aiTrainingDenied: "Entrenament d’IA: no permès",
    liveEnabled: "En directe ara",
    showLivePlayer: "Mostrar el reproductor en directe",
    hideLivePlayer: "Amagar el reproductor en directe",
    liveDescription:
      "La redacció ha habilitat aquest senyal de Cloudflare Stream. La reproducció comença quan es connecta el codificador.",
    liveFrameTitle: "En directe",
    noLive: "Sense emissió en directe",
    watch: "Mira",
    latestVideos: "Últims vídeos",
    videoDescription:
      "Vídeos i subtítols generats amb IA, lliurats mitjançant Cloudflare Stream.",
    illustrationFor: "Il·lustració per a",
    aiGeneratedImage: "Imatge generada amb Workers AI",
    openArticle: "Obert",
    premiumArticle: "Premium",
    adSupported: "Amb publicitat",
    subscriptionIncluded: "Amb subscripció",
    subscriptionTitle: "Contingut per a subscriptors",
    subscriptionDescription:
      "Aquesta notícia està inclosa en la subscripció. No té un preu humà per lectura.",
    subscriptionButton: "Accedir amb subscripció",
    advertisement: "Publicitat",
    advertisementDescription:
      "Aquest espai ajuda a finançar l’accés obert per als lectors humans.",
    agentLicenses: "Llicències per a agents",
    agentLicenseDescription:
      "Els agents utilitzen la mateixa URL que els lectors. Un bot s’interpreta com a cerca; l’entrenament es declara mitjançant una capçalera.",
    agentSearch: "Cerca agèntica",
    aiTraining: "Entrenament d’IA",
    licenseUse: "Llicenciar aquest ús",
    intentHelpLabel: "Com es captura la intenció d’ús",
    intentTitle: "Com es captura la intenció?",
    intentDescription:
      "Humans i agents comparteixen l’URL canònic. Cloudflare Bot Management, els agents coneguts o Accept: text/markdown activen la cerca; X-License-Intent: ai_training declara entrenament.",
    searchIntentExplanation:
      "La cerca agèntica lliura un resum de l’autor o generat amb Workers AI i, com a màxim, un URL d’imatge. No lliura el cos ni concedeix drets d’entrenament.",
    trainingIntentExplanation:
      "L’entrenament d’IA lliura l’article complet com a Markdown estructurat i en concedeix l’ús per entrenar o ajustar models.",
    intentLimit:
      "La intenció és una declaració de llicència auditable, no una prova tècnica del que el comprador fa després de descarregar el contingut.",
    close: "Tancar",
    searching: "Cercant a l’arxiu…",
    searchFailed: (status) => `La cerca ha fallat: ${status}`,
    noResults: "No hem trobat notícies coincidents.",
    returnHome: "Tornar a l’inici",
    unableToLoad: "No s’ha pogut carregar",
    retry: "Tornar-ho a provar",
    unexpectedError: "S’ha produït un error inesperat.",
    missingElement: (selector) => `Falta un element obligatori: ${selector}`,
  },
};
